import axios, { AxiosRequestConfig } from 'axios';
import appConfig from "../config";

const API_URL = appConfig.api.API_URL;

// Token storage keys
const AUTH_USER_KEY = 'authUser';
const REFRESH_TOKEN_KEY = 'bsv_refresh_token';
const REFRESH_EXPIRES_KEY = 'bsv_refresh_expires';
const REMEMBER_ME_KEY = 'bsv_remember_me';

// default
axios.defaults.baseURL = API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";
// Ensure the (Laravel) gateway returns JSON errors (422 etc.) instead of 302 redirects.
axios.defaults.headers.common["Accept"] = "application/json";

// Token management utilities
const TokenManager = {
  isRemembered: (): boolean => {
    return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  },

  // Returns the storage in use for this session
  getStorage: (): Storage => {
    return TokenManager.isRemembered() ? localStorage : sessionStorage;
  },

  getAuthStorage: (): Storage => {
    if (TokenManager.isRemembered() && localStorage.getItem(AUTH_USER_KEY)) return localStorage;
    if (sessionStorage.getItem(AUTH_USER_KEY)) return sessionStorage;
    if (localStorage.getItem(AUTH_USER_KEY)) return localStorage;
    return TokenManager.getStorage();
  },

  getRefreshStorage: (): Storage => {
    if (TokenManager.isRemembered() && localStorage.getItem(REFRESH_TOKEN_KEY)) return localStorage;
    if (sessionStorage.getItem(REFRESH_TOKEN_KEY)) return sessionStorage;
    if (localStorage.getItem(REFRESH_TOKEN_KEY)) return localStorage;
    return TokenManager.getAuthStorage();
  },

  getAuthUser: (): any | null => {
    try {
      const raw = TokenManager.getAuthStorage().getItem(AUTH_USER_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  updateAuthUser: (updater: any | ((authUser: any) => any)) => {
    try {
      const current = TokenManager.getAuthUser();
      if (!current) return;

      const next =
        typeof updater === "function"
          ? updater(current)
          : { ...current, ...updater };

      TokenManager.getAuthStorage().setItem(AUTH_USER_KEY, JSON.stringify(next));
    } catch (error) {
      console.error('Error updating auth user:', error);
    }
  },

  getAccessToken: (): string | null => {
    try {
      return TokenManager.getAuthUser()?.token || null;
    } catch {
      return null;
    }
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY);
  },

  isTokenExpired: (): boolean => {
    try {
      const storage = TokenManager.getAuthStorage();
      const authUser = storage.getItem(AUTH_USER_KEY);
      if (!authUser) return false;
      const expiresAt = JSON.parse(authUser)?.expires_at;
      if (!expiresAt) return false;
      const bufferTime = 5 * 60 * 1000; // 5-minute buffer
      return Date.now() >= new Date(expiresAt).getTime() - bufferTime;
    } catch {
      return false;
    }
  },

  isRefreshTokenExpired: (): boolean => {
    const storage = TokenManager.getRefreshStorage();
    const expiresAt = storage.getItem(REFRESH_EXPIRES_KEY);
    if (!expiresAt) return false;
    return Date.now() >= new Date(expiresAt).getTime();
  },

  setTokens: (authResponse: any, rememberMe: boolean = false) => {
    try {
      // Persist remember me preference
      if (rememberMe) {
        localStorage.setItem(REMEMBER_ME_KEY, 'true');
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY);
      }

      const storage = rememberMe ? localStorage : sessionStorage;
      const otherStorage = rememberMe ? sessionStorage : localStorage;
      otherStorage.removeItem(AUTH_USER_KEY);
      otherStorage.removeItem(REFRESH_TOKEN_KEY);
      otherStorage.removeItem(REFRESH_EXPIRES_KEY);

      const authData = { ...authResponse };

      if (authData.refresh_token) {
        storage.setItem(REFRESH_TOKEN_KEY, authData.refresh_token);
        if (authData.refresh_expires_in) {
          const refreshExpiresAt = new Date(Date.now() + authData.refresh_expires_in * 1000).toISOString();
          storage.setItem(REFRESH_EXPIRES_KEY, refreshExpiresAt);
        }
        delete authData.refresh_token;
        delete authData.refresh_expires_in;
      }

      if (authData.expires_in) {
        authData.expires_at = new Date(Date.now() + authData.expires_in * 1000).toISOString();
      }

      storage.setItem(AUTH_USER_KEY, JSON.stringify(authData));
    } catch (error) {
      console.error('Error storing tokens:', error);
    }
  },

  // Update only the access token after a successful refresh
  updateAccessToken: (newToken: string, expiresIn?: number) => {
    try {
      const storage = TokenManager.getAuthStorage();
      const authUser = storage.getItem(AUTH_USER_KEY);
      if (!authUser) return;
      const parsed = JSON.parse(authUser);
      parsed.token = newToken;
      if (expiresIn) {
        parsed.expires_in = expiresIn;
        parsed.expires_at = new Date(Date.now() + expiresIn * 1000).toISOString();
      }
      storage.setItem(AUTH_USER_KEY, JSON.stringify(parsed));
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    } catch (error) {
      console.error('Error updating access token:', error);
    }
  },

  getUserType: (): string | null => {
    try {
      return TokenManager.getAuthUser()?.user_type ?? null;
    } catch {
      return null;
    }
  },

  getPermissions: (): string[] => {
    try {
      const permissions = TokenManager.getAuthUser()?.admin?.admin_group?.permissions;
      if (!Array.isArray(permissions)) return [];
      return permissions.map((p: any) => p?.slug).filter(Boolean);
    } catch {
      return [];
    }
  },

  isSuperAdmin: (): boolean => {
    return TokenManager.getPermissions().includes("super_admin");
  },

  clearTokens: () => {
    // Clear from both storages to be safe
    sessionStorage.removeItem(AUTH_USER_KEY);
    sessionStorage.removeItem("refreshToken"); // legacy cleanup
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_EXPIRES_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_EXPIRES_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
    delete axios.defaults.headers.common["Authorization"];
  }
};

// Set initial authorization header
const token = TokenManager.getAccessToken();
if (token) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

const forceLogout = () => {
  TokenManager.clearTokens();
  if (typeof window !== "undefined") {
    if (!window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
  }
};

// Refresh state — prevents multiple simultaneous refresh calls
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
};

const doTokenRefresh = async (): Promise<string> => {
  const refreshToken = TokenManager.getRefreshToken();
  if (!refreshToken || TokenManager.isRefreshTokenExpired()) {
    forceLogout();
    throw new Error("Session expired. Please log in again.");
  }

  const refreshEndpoint = "/api/v1/auth/admin/refresh";

  // Call refresh endpoint directly with axios to bypass interceptors
  const response = await axios.post(
    refreshEndpoint,
    { refresh_token: refreshToken },
    { headers: { Authorization: undefined } } as any
  );

  const data = response.data ?? response;
  const newToken: string = data?.token;
  if (!newToken) throw new Error("No token in refresh response");

  TokenManager.updateAccessToken(newToken, data?.expires_in);

  // Update stored refresh token if a new one is issued
  if (data?.refresh_token) {
    const storage = TokenManager.getRefreshStorage();
    storage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
    if (data?.refresh_expires_in) {
      storage.setItem(
        REFRESH_EXPIRES_KEY,
        new Date(Date.now() + data.refresh_expires_in * 1000).toISOString()
      );
    }
  }

  return newToken;
};

// Request interceptor - attach token, proactively refresh if near expiry
axios.interceptors.request.use(
  async (request) => {
    request.baseURL = API_URL;

    const isAuthEndpoint =
      request.url?.includes('/login') ||
      request.url?.includes('/register') ||
      request.url?.includes('/refresh');

    if (isAuthEndpoint) return request;

    const accessToken = TokenManager.getAccessToken();
    if (!accessToken) return request;

    // Proactively refresh if the access token is expired/near-expiry and we have a valid refresh token
    if (TokenManager.isTokenExpired() && TokenManager.getRefreshToken() && !TokenManager.isRefreshTokenExpired()) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await doTokenRefresh();
          processQueue(null, newToken);
          (request.headers as any).Authorization = `Bearer ${newToken}`;
        } catch (err) {
          processQueue(err, null);
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Another refresh is in progress — wait for it
        const newToken = await new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
        (request.headers as any).Authorization = `Bearer ${newToken}`;
      }
      return request;
    }

    if (TokenManager.isTokenExpired()) {
      // Token expired and no valid refresh token
      forceLogout();
      return Promise.reject({ message: "Session expired. Please log in again." });
    }

    if (!request.headers) (request as any).headers = {};
    (request.headers as any).Authorization = `Bearer ${accessToken}`;
    return request;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 with refresh retry, then surface errors
axios.interceptors.response.use(
  (response) => {
    // Blob responses (CSV/file downloads) need the full response: the filename
    // lives in the Content-Disposition header, which unwrapping would discard.
    if (response.config?.responseType === "blob") return response;
    return response.data ? response.data : response;
  },
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint =
      originalRequest?.url?.includes('/login') ||
      originalRequest?.url?.includes('/register') ||
      originalRequest?.url?.includes('/refresh');

    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest?._retry) {
      if (isRefreshing) {
        // Queue this request until the current refresh finishes
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await doTokenRefresh();
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.data) {
      return Promise.reject(error.response.data);
    }

    let message;
    switch (error.response?.status) {
      case 500: message = "Internal Server Error"; break;
      case 401: message = "Invalid credentials"; break;
      case 404: message = "Sorry! the data you are looking for could not be found"; break;
      case 403: message = "Access forbidden"; break;
      default:  message = error.message || error;
    }
    return Promise.reject({ message });
  }
);

/**
 * Sets the default authorization
 * @param {string} token
 */
const setAuthorization = (token: string) => {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

/**
 * Encodes params for report endpoints. Unlike APIClient.get's ad-hoc string
 * concatenation this URL-encodes values and drops empties, which matters for
 * filters carrying commas, spaces or symbols.
 */
const buildQueryString = (params?: Record<string, any>): string => {
  if (!params) return "";

  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.append(key, Array.isArray(value) ? value.join(",") : String(value));
  });

  const query = search.toString();
  return query ? `?${query}` : "";
};

class APIClient {
  /**
   * Fetches data from the given URL
   * Returns the data directly (not AxiosResponse) due to interceptor
   */
  get = (url: string, params?: any): Promise<any> => {
    let response: Promise<any>;

    let paramKeys: string[] = [];

    if (params) {
      Object.keys(params).map(key => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString = paramKeys && paramKeys.length ? paramKeys.join('&') : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };

  /**
   * GET with properly encoded query params. Prefer this over get() when values
   * can contain spaces or symbols (search terms, date ranges, filter lists).
   */
  getQuery = (url: string, params?: Record<string, any>): Promise<any> => {
    return axios.get(`${url}${buildQueryString(params)}`);
  };

  /**
   * Downloads a file and hands it to the browser as a save dialog.
   *
   * Goes through the shared axios instance on purpose, so the 401-refresh
   * interceptor still applies - a plain <a href> would 401, because the JWT is
   * sent as an Authorization header rather than a cookie.
   */
  download = async (url: string, params?: Record<string, any>, fallbackName = "report.csv"): Promise<void> => {
    const query = buildQueryString(params);

    let response: any;
    try {
      response = await axios.get(`${url}${query}`, { responseType: "blob" });
    } catch (err: any) {
      // A failed download still returns JSON, but as a Blob because of
      // responseType - read it back so the real message reaches the user.
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try {
          throw new Error(JSON.parse(text)?.message || "Download failed.");
        } catch (parseErr: any) {
          throw new Error(parseErr?.message || "Download failed.");
        }
      }
      throw err;
    }

    const disposition = response?.headers?.["content-disposition"] || "";
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : fallbackName;

    const href = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(href);
  };

  /**
   * Posts the given data to the URL
   * Returns the data directly (not AxiosResponse) due to interceptor
   */
  create = (url: string, data: any): Promise<any> => {
    return axios.post(url, data);
  };

   /**
   * Posts FormData to the URL (for file uploads)
   */
  createWithFormData = (url: string, data: FormData): Promise<any> => {
    return axios.post(url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  /**
   * Updates data
   * Returns the data directly (not AxiosResponse) due to interceptor
   */
  update = (url: string, data: any): Promise<any> => {
    return axios.patch(url, data);
  };

  put = (url: string, data: any): Promise<any> => {
    return axios.put(url, data);
  };

  /**
   * Deletes data
   * Returns the data directly (not AxiosResponse) due to interceptor
   */
  delete = (url: string, config?: AxiosRequestConfig): Promise<any> => {
    return axios.delete(url, { ...config });
  };
}

const getLoggedinUser = () => {
  return TokenManager.getAuthUser();
};

export { 
  APIClient, 
  setAuthorization, 
  getLoggedinUser, 
  TokenManager 
};
